/**
 * CompanyMemberPage · Web (B2B — Fev/2026)
 *
 * Página dedicada para `user` normal que faz parte de uma empresa
 * (`user.role === 'user'` + `user.company_id` populado).
 *
 * Diferença clara do `CompanyDashboard`:
 *   - Marshall (`company_admin`/`company_staff`) → `/empresa` (dashboard COMPLETO,
 *     com upload rotas, gestão membros, reset password, KPIs, criar eventos, mural)
 *   - Membro normal → `/empresa/membro` (esta página, READ-ONLY em quase tudo)
 *
 * O que o membro vê:
 *   1. Header com logo/nome da empresa (cache-buster)
 *   2. Próximo evento em destaque (título, data, mapa do ponto de encontro,
 *      rota associada)
 *   3. Mural da empresa (pode ler, comentar, reagir; não pode apagar posts alheios)
 *   4. Rotas privadas da empresa (com "Ver rota" → RouteViewerModal)
 *   5. Membros da equipa (só lista visual, sem acções)
 *   6. Botão "Rotas públicas" → `/dashboard` (rotas globais e do resto do mundo)
 *   7. Botão "Sair"
 *
 * Não tem: upload/edit/delete rotas · criar/cancelar eventos · gestão members
 *          · reset password · export XLSX · KPIs agregados
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  Building2, LogOut, Route as RouteIcon, Eye, Loader2, RefreshCw,
  Calendar, MapPin, Users, ArrowRight, MessageSquare, Star,
} from 'lucide-react';
import RouteViewerModal from '@/components/RouteViewerModal';
import 'leaflet/dist/leaflet.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Paleta Rugged consistente com o resto do B2B
const C = {
  bg: '#2B2A25',
  bg2: '#241E19',
  bg3: '#0F0D0B',
  brand: '#E87A2A',
  brandDim: 'rgba(232,122,42,0.16)',
  sand: '#C4A275',
  sandLine: 'rgba(196,162,117,0.18)',
  ink: '#F2EBDD',
  inkDim: '#8A7A63',
  cardBg: 'rgba(26,22,19,0.82)',
  green: '#3FA34D',
};

// Ícone laranja custom para o ponto de encontro (mesma paleta que MeetingPointPicker)
const orangePin = L.icon({
  iconUrl:
    'data:image/svg+xml;utf8,' +
    encodeURIComponent(
      '<svg xmlns="http://www.w3.org/2000/svg" width="28" height="42" viewBox="0 0 24 36"><path d="M12 0c-6.6 0-12 5.4-12 12 0 9 12 24 12 24s12-15 12-24c0-6.6-5.4-12-12-12z" fill="#E87A2A" stroke="#F2EBDD" stroke-width="2"/><circle cx="12" cy="12" r="4" fill="#F2EBDD"/></svg>',
    ),
  iconSize: [28, 42],
  iconAnchor: [14, 42],
});

const CompanyMemberPage = () => {
  const navigate = useNavigate();
  const { user, logout, loading: authLoading } = useAuth();
  const [company, setCompany] = useState(null);
  const [events, setEvents] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewerRouteId, setViewerRouteId] = useState(null);
  const [savedRoutes, setSavedRoutes] = useState([]);
  const [savingId, setSavingId] = useState(null);

  const companyId = user?.company_id;

  const fetchAll = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      // Fev/2026 — Promise.allSettled em vez de Promise.all: qualquer 403/500
      // num endpoint não bloqueia os outros. Membro comum recebe 403 em
      // /companies/{id} e /members (policy) — mas rotas + eventos vêm 200.
      const results = await Promise.allSettled([
        axios.get(`${API}/companies/${companyId}`, { withCredentials: true }),
        axios.get(`${API}/companies/${companyId}/events?scope=upcoming`, { withCredentials: true }),
        axios.get(`${API}/routes`, { withCredentials: true }),
        axios.get(`${API}/companies/${companyId}/members`, { withCredentials: true }),
        axios.get(`${API}/users/me/favorites`, { withCredentials: true }),
      ]);
      const [companyRes, eventsRes, routesRes, membersRes, savedRes] = results;
      if (companyRes.status === 'fulfilled') setCompany(companyRes.value.data);
      if (eventsRes.status === 'fulfilled') setEvents(eventsRes.value.data || []);
      if (routesRes.status === 'fulfilled') {
        setRoutes((routesRes.value.data || []).filter((r) => r.company_id === companyId && !r.is_deleted));
      }
      if (membersRes.status === 'fulfilled') setMembers(membersRes.value.data || []);
      if (savedRes.status === 'fulfilled') {
        // Endpoint existente devolve lista de rotas favoritas (objectos ou ids).
        const favs = savedRes.value.data || [];
        setSavedRoutes(favs.map((r) => (typeof r === 'string' ? r : r.route_id)).filter(Boolean));
      }
      // Diagnóstico: se TODOS falharem, redireccionar (sessão expirada)
      const anyAuth = results.some(
        (r) => r.status === 'rejected' && r.reason?.response?.status === 401,
      );
      if (anyAuth) navigate('/login');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro a carregar painel');
    } finally {
      setLoading(false);
    }
  }, [companyId, navigate]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    // Marshalls têm o painel completo — se cair aqui, redireciona.
    if (['company_admin', 'company_staff'].includes(user.role)) {
      navigate('/empresa');
      return;
    }
    // Sem company_id → não é membro de empresa
    if (!user.company_id) {
      navigate('/dashboard');
      return;
    }
    fetchAll();
  }, [user, authLoading, navigate, fetchAll]);

  const doLogout = async () => { await logout(); navigate('/login'); };

  // Fev/2026 — Reaproveita o sistema de FAVORITOS existente (mesmo que HomeScreen
  // mobile usa). Endpoint `POST /api/routes/{id}/favorite` faz toggle e devolve
  // `{route_id, favorited}`. Cache local para UX imediato.
  const toggleSaved = async (routeId) => {
    setSavingId(routeId);
    try {
      const { data } = await axios.post(`${API}/routes/${routeId}/favorite`, {}, { withCredentials: true });
      const nowFav = !!data?.favorited;
      setSavedRoutes((prev) => nowFav ? [...new Set([...prev, routeId])] : prev.filter((r) => r !== routeId));
      toast.success(nowFav
        ? 'Rota guardada — próxima abertura da app pré-carrega offline'
        : 'Rota removida dos favoritos');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao guardar');
    } finally {
      setSavingId(null);
    }
  };

  const nextEvent = events[0]; // já vem ordenado por starts_at ASC do backend

  if (authLoading || (loading && !company)) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center" style={{ backgroundColor: C.bg }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: C.brand }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full" style={{ backgroundColor: C.bg }} data-testid="company-member-page">
      {/* Header */}
      <header
        className="sticky top-0 z-30 backdrop-blur"
        style={{ backgroundColor: 'rgba(26,22,19,0.9)', borderBottom: `1px solid ${C.sandLine}` }}
      >
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden"
              style={{ backgroundColor: C.brandDim, border: `1px solid ${C.brand}` }}
            >
              {company?.logo_path ? (
                <img
                  src={`${BACKEND_URL}/${company.logo_path}?t=${encodeURIComponent(company.updated_at || '')}`}
                  alt={company.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <Building2 className="w-5 h-5" style={{ color: C.brand }} />
              )}
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest font-bold" style={{ color: C.inkDim }}>Membro</div>
              <div className="text-lg font-bold leading-tight" style={{ color: C.ink }}>{company?.name || '—'}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/dashboard')}
              className="px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5 transition"
              style={{ color: C.ink, border: `1px solid ${C.sandLine}` }}
              title="Ver rotas públicas (globais)"
              data-testid="member-public-routes-btn"
            >
              🌍 Rotas públicas <ArrowRight className="w-3 h-3" />
            </button>
            <button
              onClick={fetchAll}
              className="px-2.5 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1 transition"
              style={{ color: C.ink, border: `1px solid ${C.sandLine}` }}
              data-testid="member-refresh-btn"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
            {/* Sair do painel → volta ao principal com mapa (a sessão continua). */}
            <button
              onClick={() => navigate('/dashboard')}
              className="px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5 transition"
              style={{ backgroundColor: C.brand, color: '#fff' }}
              data-testid="member-exit-btn"
              title="Voltar à página principal com mapa"
            >
              <LogOut className="w-3.5 h-3.5" /> Sair
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-5">
        {/* Próximo evento em destaque */}
        {nextEvent ? (
          <div
            className="rounded-xl border overflow-hidden"
            style={{ backgroundColor: C.cardBg, borderColor: C.brand, borderWidth: 2 }}
            data-testid="member-next-event"
          >
            <div className="px-4 py-3 flex items-center gap-2" style={{ borderBottom: `1px solid ${C.sandLine}` }}>
              <Calendar className="w-4 h-4" style={{ color: C.brand }} />
              <div className="text-xs font-black uppercase tracking-widest" style={{ color: C.brand }}>
                Próximo evento
              </div>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <div className="text-xl font-bold" style={{ color: C.ink }}>{nextEvent.title}</div>
                <div className="text-sm mt-1" style={{ color: C.inkDim }}>
                  📅 {new Date(nextEvent.starts_at).toLocaleString('pt-PT', { dateStyle: 'full', timeStyle: 'short' })}
                </div>
                {nextEvent.meeting_name && (
                  <div className="text-sm mt-1 flex items-center gap-1.5" style={{ color: C.ink }}>
                    <MapPin className="w-3.5 h-3.5" style={{ color: C.brand }} /> {nextEvent.meeting_name}
                  </div>
                )}
                {nextEvent.description && (
                  <div className="text-sm mt-2 opacity-90" style={{ color: C.ink }}>{nextEvent.description}</div>
                )}
              </div>
              {typeof nextEvent.meeting_lat === 'number' && typeof nextEvent.meeting_lng === 'number' && (
                <div style={{ height: 240, borderRadius: 8, overflow: 'hidden', border: `1px solid ${C.sandLine}` }}>
                  <MapContainer
                    center={[nextEvent.meeting_lat, nextEvent.meeting_lng]}
                    zoom={13}
                    style={{ width: '100%', height: '100%' }}
                    scrollWheelZoom={false}
                  >
                    <TileLayer
                      attribution="&copy; OSM"
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <Marker
                      position={[nextEvent.meeting_lat, nextEvent.meeting_lng]}
                      icon={orangePin}
                    />
                  </MapContainer>
                </div>
              )}
              {nextEvent.route_id && (
                <button
                  onClick={() => setViewerRouteId(nextEvent.route_id)}
                  className="w-full py-2.5 rounded-md text-sm font-bold flex items-center justify-center gap-2"
                  style={{ backgroundColor: C.brand, color: '#fff' }}
                  data-testid="member-view-event-route"
                >
                  <Eye className="w-4 h-4" /> Ver rota do evento
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="rounded-xl border p-6 text-center text-sm" style={{ backgroundColor: C.cardBg, borderColor: C.sandLine, color: C.inkDim }} data-testid="member-no-events">
            🗓️ Sem eventos agendados. O teu marshall irá anunciar por aqui o próximo passeio.
          </div>
        )}

        {/* Próximos eventos secundários (2º em diante) */}
        {events.length > 1 && (
          <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: C.cardBg, borderColor: C.sandLine }} data-testid="member-more-events">
            <div className="px-4 py-3 border-b" style={{ borderColor: C.sandLine }}>
              <div className="text-xs font-bold uppercase tracking-wider" style={{ color: C.ink }}>Também em agenda</div>
            </div>
            <div className="divide-y" style={{ borderColor: C.sandLine }}>
              {events.slice(1, 6).map((evt) => (
                <div key={evt.event_id} className="px-4 py-2.5 flex items-center justify-between" data-testid={`member-event-${evt.event_id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate" style={{ color: C.ink }}>{evt.title}</div>
                    <div className="text-xs mt-0.5" style={{ color: C.inkDim }}>
                      📅 {new Date(evt.starts_at).toLocaleString('pt-PT', { dateStyle: 'short', timeStyle: 'short' })}
                      {evt.meeting_name && <span className="ml-2 truncate">📍 {evt.meeting_name}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Rotas da empresa (read-only, só Ver) */}
        <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: C.cardBg, borderColor: C.sandLine }} data-testid="member-routes-panel">
          <div className="px-4 py-3 flex items-center gap-2 border-b" style={{ borderColor: C.sandLine }}>
            <RouteIcon className="w-4 h-4" style={{ color: C.brand }} />
            <div className="text-sm font-bold uppercase tracking-wider flex-1" style={{ color: C.ink }}>
              Rotas da empresa ({routes.length})
            </div>
          </div>
          {routes.length === 0 ? (
            <div className="p-6 text-center text-sm" style={{ color: C.inkDim }}>Ainda não há rotas partilhadas.</div>
          ) : (
            <div className="divide-y" style={{ borderColor: C.sandLine }}>
              {routes.map((r) => (
                <div key={r.route_id} className="px-4 py-3 flex items-center justify-between gap-2 flex-wrap" data-testid={`member-route-${r.route_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate" style={{ color: C.ink }}>{r.name}</div>
                    <div className="text-xs mt-0.5" style={{ color: C.inkDim }}>
                      {r.difficulty ? `${r.difficulty} · ` : ''}
                      {r.distance_km ? `${Number(r.distance_km).toFixed(1)} km` : ''}
                      {r.region ? ` · ${r.region}` : ''}
                    </div>
                  </div>
                  <button
                    onClick={() => toggleSaved(r.route_id)}
                    disabled={savingId === r.route_id}
                    className="px-3 py-1.5 rounded-md text-xs font-bold flex items-center gap-1.5 transition disabled:opacity-60"
                    style={{
                      backgroundColor: savedRoutes.includes(r.route_id) ? C.sand : 'rgba(0,0,0,0.35)',
                      color: savedRoutes.includes(r.route_id) ? C.bg3 : C.sand,
                      border: `1px solid ${C.sand}`,
                    }}
                    title={savedRoutes.includes(r.route_id) ? 'Remover do offline mobile' : 'Guardar para offline na app'}
                    data-testid={`member-save-route-${r.route_id}`}
                  >
                    {savingId === r.route_id
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <Star className="w-3.5 h-3.5" fill={savedRoutes.includes(r.route_id) ? C.bg3 : 'none'} />}
                    {savedRoutes.includes(r.route_id) ? 'Guardada' : 'Guardar'}
                  </button>
                  <button
                    onClick={() => setViewerRouteId(r.route_id)}
                    className="px-3 py-1.5 rounded-md text-xs font-bold flex items-center gap-1.5 transition"
                    style={{ backgroundColor: C.brand, color: '#fff' }}
                    data-testid={`member-view-route-${r.route_id}`}
                  >
                    <Eye className="w-3.5 h-3.5" /> Ver
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Equipa */}
        <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: C.cardBg, borderColor: C.sandLine }} data-testid="member-team-panel">
          <div className="px-4 py-3 flex items-center gap-2 border-b" style={{ borderColor: C.sandLine }}>
            <Users className="w-4 h-4" style={{ color: C.brand }} />
            <div className="text-sm font-bold uppercase tracking-wider flex-1" style={{ color: C.ink }}>
              A tua equipa ({members.length})
            </div>
          </div>
          <div className="p-3 flex flex-wrap gap-2">
            {members.map((mb) => (
              <div key={mb.user_id} className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full" style={{ backgroundColor: 'rgba(0,0,0,0.35)', border: `1px solid ${C.sandLine}` }}>
                <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold overflow-hidden" style={{ backgroundColor: C.brandDim, color: C.brand }}>
                  {mb.picture ? <img src={mb.picture} alt={mb.name} className="w-full h-full object-cover" /> : (mb.name || '?').slice(0, 2).toUpperCase()}
                </div>
                <div className="text-xs" style={{ color: C.ink }}>{mb.name || mb.email}</div>
                {mb.role === 'company_admin' && <span className="text-[9px] px-1.5 py-0.5 rounded uppercase font-bold" style={{ backgroundColor: C.brandDim, color: C.brand }}>Marshall</span>}
                {mb.role === 'company_staff' && <span className="text-[9px] px-1.5 py-0.5 rounded uppercase font-bold" style={{ backgroundColor: 'rgba(196,162,117,0.20)', color: C.sand }}>Staff</span>}
              </div>
            ))}
          </div>
        </div>

        {/* Placeholder mural — chamada de atenção para o mural existir. Ao clicar
             usa RouteViewer para abrir a versão mural no CompanyDashboard futura;
             por agora só mostra info. */}
        <div className="rounded-xl border p-4 flex items-center gap-3" style={{ backgroundColor: C.cardBg, borderColor: C.sandLine }} data-testid="member-mural-teaser">
          <MessageSquare className="w-5 h-5" style={{ color: C.brand }} />
          <div className="text-xs" style={{ color: C.inkDim }}>
            Mural da equipa disponível — acede via o teu marshall para ver posts recentes.
          </div>
        </div>
      </main>

      {viewerRouteId && (
        <RouteViewerModal
          routeId={viewerRouteId}
          open={!!viewerRouteId}
          onClose={() => setViewerRouteId(null)}
          user={user}
          liveOverlay={true}
          liveOverlayMode="company"
          companyId={companyId}
        />
      )}
    </div>
  );
};

export default CompanyMemberPage;
